import { Injectable, inject } from '@angular/core';
import { Dialog, DialogConfig, DialogRef } from '@angular/cdk/dialog';
import { ComponentType } from '@angular/cdk/portal';
import { ConfirmDialogComponent, ConfirmDialogData } from './confirm-dialog.component';

/**
 * Drop-in replacement for MatDialog, built on the headless @angular/cdk/dialog.
 * Applies our design-system chrome (panel + backdrop) by default; CDK handles
 * the hard parts (overlay, focus trap, ESC-to-close, scroll-block).
 */
@Injectable({ providedIn: 'root' })
export class DialogService {
  private readonly dialog = inject(Dialog);

  open<R = unknown, D = unknown, C = unknown>(
    component: ComponentType<C>,
    config?: DialogConfig<D, DialogRef<R, C>>,
  ): DialogRef<R, C> {
    return this.dialog.open<R, D, C>(component, {
      panelClass: 'bolt-dialog-panel',
      backdropClass: 'bolt-backdrop',
      hasBackdrop: true,
      autoFocus: 'first-tabbable',
      restoreFocus: true,
      ...config,
    });
  }

  /** Convenience confirm() returning a promise that resolves true/false. */
  confirm(data: ConfirmDialogData): Promise<boolean> {
    const ref = this.open<boolean, ConfirmDialogData, ConfirmDialogComponent>(
      ConfirmDialogComponent,
      { data },
    );
    return new Promise((resolve) => {
      ref.closed.subscribe((result) => resolve(result === true));
    });
  }
}
